export interface PageSection {
  instanceId: string;
  semanticRole: string;
  label: string;
}

export interface UnresolvableSection {
  instanceId: string;
  reason: string;
}

export interface SectionScan {
  sections: PageSection[];
  unresolvable: UnresolvableSection[];
  unclaimed: number;
  unavailable: string | null;
}

const blocked = (reason: string): SectionScan => ({
  sections: [],
  unresolvable: [],
  unclaimed: 0,
  unavailable: reason,
});

const labelOf = (id: string) =>
  id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const readMeta = (copyMeta: unknown) => {
  const meta = (copyMeta ?? {}) as {
    layout_engine?: unknown;
    sections?: unknown;
  };
  return {
    layoutEngine:
      typeof meta.layout_engine === 'string' ? meta.layout_engine : '',
    declared: Array.isArray(meta.sections)
      ? meta.sections
          .map((s) => (typeof s === 'string' ? s.trim().toLowerCase() : ''))
          .filter((s) => s !== '')
      : [],
  };
};

export function scanSections(html: string, copyMeta: unknown): SectionScan {
  const { layoutEngine, declared } = readMeta(copyMeta);

  if (layoutEngine !== 'dsh') {
    return blocked(
      layoutEngine
        ? `This page was built by the ${layoutEngine} engine. Only DeepSeek-lane pages carry per-section ids, so favoriting is not supported here yet.`
        : 'This page records no layout engine, so it predates the DeepSeek lane and carries no per-section ids. Favoriting is not supported here yet.',
    );
  }

  if (declared.length === 0) {
    return blocked(
      'This page records no section list, so there is nothing that says which bands it is meant to have. Nothing is offered rather than a guess from the markup.',
    );
  }

  if (!html || !html.trim()) {
    return blocked('This page has no stored HTML.');
  }

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return blocked('The stored HTML for this page could not be parsed.');
  }

  // The same band element set DraftLayout recognises, so a declared `header` or
  // `footer` that the writer emits as a `<header>`/`<footer>` element resolves.
  // `declared` is lowercased; key the DOM ids the same way so the lookup is
  // case-insensitive, matching scopeBand's ID handling. Otherwise id="Hero"
  // would never match declared "hero" and would be reported as missing.
  // Only a TOP-LEVEL band counts (not nested inside another band element), the same boundary
  // the band swap's scanner uses, so the picker never offers a band the swap cannot find.
  const BAND = 'section[id], header[id], footer[id], nav[id], aside[id]';
  const counts = new Map<string, number>();
  const nested = new Set<string>();
  Array.from(doc.querySelectorAll(BAND)).forEach((el) => {
    const id = (el.getAttribute('id') || '').trim().toLowerCase();
    if (id === '') return;
    if (el.parentElement?.closest(BAND)) { nested.add(id); return; }
    counts.set(id, (counts.get(id) ?? 0) + 1);
  });

  const sections: PageSection[] = [];
  const unresolvable: UnresolvableSection[] = [];

  declared.forEach((id) => {
    const found = counts.get(id) ?? 0;
    if (found === 1) {
      sections.push({
        instanceId: id,
        semanticRole: id,
        label: labelOf(id),
      });
      return;
    }
    unresolvable.push({
      instanceId: id,
      reason:
        found > 1
          ? `the stored HTML carries ${found} elements with that id, so one cannot be told from another`
          : nested.has(id)
            ? 'the element with that id sits inside another band, so it is not a band of its own'
            : 'the page lists this band but no element in the stored HTML carries that id',
    });
  });

  const unclaimed = [...counts.keys()].filter(
    (id) => !declared.includes(id),
  ).length;

  if (sections.length === 0) {
    return {
      sections: [],
      unresolvable,
      unclaimed,
      unavailable:
        'None of the bands this page lists could be matched to an element in its stored HTML.',
    };
  }

  return { sections, unresolvable, unclaimed, unavailable: null };
}
